import PageLayout from "@/components/feature/PageLayout";

const sectionClass = "flex flex-col gap-3";
const headingClass = "font-fraunces text-[#2B2B2B] text-xl font-medium";
const linkClass = "text-[#3C6FF8] hover:underline";

export default function TermsConditions() {
  return (
    <PageLayout>
      <section className="w-full py-16 md:py-24">
        <article className="w-full px-6 md:px-12 lg:px-20 max-w-3xl mx-auto">
          <h1 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-4">
            Terms &amp; Conditions
          </h1>
          <p className="font-manrope text-[#2B2B2B]/60 text-sm mb-12">Last updated: August 21, 2026</p>

          <div className="flex flex-col gap-9 font-manrope text-[15px] text-[#2B2B2B]/80 leading-relaxed">
            <div className={sectionClass}>
              <h2 className={headingClass}>Acceptance and Website Use</h2>
              <p>
                These Terms &amp; Conditions govern use of the public website operated by Perspective Therapy Mental Health Counseling, PLLC ("Perspective Therapy," "we," "us," or "our") at perspectivetherapy.com. By using the website, you agree to these terms. If you do not agree, please do not use the website.
              </p>
              <p>
                These terms apply only to the public website and related public communications. They do not replace or modify clinical consent forms, client-portal terms, privacy notices, or other agreements governing therapy services.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Informational Content; No Therapist-Client Relationship</h2>
              <p>
                Website and blog content is provided for general informational and educational purposes. It is not medical or mental health advice, diagnosis, treatment, crisis support, or a substitute for consultation with a qualified professional.
              </p>
              <p>
                Browsing this website, using the website concierge, subscribing to blog updates, or submitting a general inquiry does not create a therapist-client relationship. A therapist-client relationship is established only through the practice's separate intake, consent, and acceptance process.
              </p>
              <p>
                This website is not an emergency service. If you or someone else may be in immediate danger, call 911 or go to the nearest emergency room. In the United States, you may also call or text 988 for crisis support.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Intellectual Property</h2>
              <p>
                The website's design, text, graphics, logos, images, and other original content are owned by Perspective Therapy or used with permission and are protected by applicable intellectual-property laws. You may view and print reasonable portions for personal, noncommercial use. You may not reproduce, distribute, modify, sell, or commercially exploit site content without prior written permission, except as permitted by law.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Acceptable Use</h2>
              <p>You agree not to:</p>
              <ul className="list-disc pl-5 flex flex-col gap-1">
                <li>use the website for unlawful, fraudulent, threatening, or abusive activity;</li>
                <li>attempt to gain unauthorized access to the website, systems, accounts, or data;</li>
                <li>introduce malicious code or interfere with the site's operation or security;</li>
                <li>impersonate another person or submit information you are not authorized to provide; or</li>
                <li>use automated means to scrape or overload the website, except ordinary search-engine indexing.</li>
              </ul>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Contact Forms and Communications</h2>
              <p>
                You are responsible for ensuring information you submit is accurate and that you are authorized to provide it. Public website forms, chat, ordinary email, and SMS should not be used to send sensitive clinical details or to request emergency or crisis assistance. We may respond using the contact information you provide.
              </p>
              <p>
                Optional blog emails, necessary or transactional communications authorized in another context, optional SMS reminders, and treatment consent are distinct. Consent to one does not automatically provide consent to any other.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Optional Blog Updates</h2>
              <p>
                Blog-update subscriptions are voluntary and are not a condition of receiving therapy services. Email frequency may vary. Subscribers may unsubscribe at any time using the unsubscribe mechanism provided in those emails. Optional public website content emails are not clinical communications. Unsubscribing from them does not prevent necessary transactional or account communications that are separately authorized or appropriate.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>SMS Messaging Terms</h2>
              <p>
                By consenting to SMS communications from <strong>Perspective Therapy</strong>, you may receive appointment reminders, scheduling and rescheduling updates, client account or portal administrative notices, other administrative communications, and conversational replies from authorized practice staff where appropriate. Message frequency varies depending on appointments and administrative communication needs. Message and data rates may apply. Reply <strong>STOP</strong> to opt out at any time. Reply <strong>HELP</strong> for assistance.
              </p>
              <p>
                SMS consent is optional and is not required as a condition of receiving services. After opting out, you will receive no further SMS messages unless you later opt back in. SMS must not be used for emergencies, crisis support, therapy, or substantive clinical communication. You are responsible for providing and maintaining a current mobile number. Delivery depends on mobile-carrier and network availability and is not guaranteed.
              </p>
              <p>
                For support, reply <strong>HELP</strong>, call <a className={linkClass} href="tel:+18389108150">(838) 910-8150</a>, or email <a className={linkClass} href="mailto:Info@perspectivetherapyNY.com">Info@perspectivetherapyNY.com</a>. SMS consent and mobile information are governed by our <a className={linkClass} href="/privacy-policy">Privacy Policy</a>, including its limits on sharing mobile information. Service providers acting on our behalf may process information as reasonably necessary to deliver requested messages.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>External Links and Services</h2>
              <p>
                The website may link to third-party websites or services, including booking and client-portal services. Links are provided for convenience and do not make us responsible for external content, availability, security, or practices. Your use of an external service is governed by its own terms and policies.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Website Availability and Disclaimers</h2>
              <p>
                We work to keep website information useful and current, but information may contain errors or become outdated, and services, clinician availability, and eligibility may change. To the extent permitted by law, the public website is provided "as is" and "as available" without warranties of uninterrupted availability, error-free operation, or suitability for a particular purpose. Nothing in these terms excludes rights or responsibilities that cannot lawfully be excluded.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Changes to These Terms</h2>
              <p>
                We may update these terms to reflect changes to the website, our practices, or applicable requirements. Revised terms will be posted here with an updated date. Continued use after an update means you accept the revised terms.
              </p>
            </div>

            <div className={sectionClass}>
              <h2 className={headingClass}>Contact Us</h2>
              <p>Questions about these terms may be directed to:</p>
              <address className="not-italic">
                Perspective Therapy Mental Health Counseling, PLLC<br />
                4 Gilder Street, Suite 2<br />
                Larchmont, NY 10538<br />
                <a className={linkClass} href="mailto:Info@perspectivetherapyNY.com">Info@perspectivetherapyNY.com</a><br />
                <a className={linkClass} href="tel:+18389108150">(838) 910-8150</a>
              </address>
            </div>
          </div>
        </article>
      </section>
    </PageLayout>
  );
}
